import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, message } from 'antd';
import Sdk from 'casdoor-js-sdk';

const Callback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasProcessed.current) {
      return;
    }

    const handleCallback = async () => {
      hasProcessed.current = true;
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code || !state) {
          message.error('Invalid callback parameters');
          navigate('/login', { replace: true });
          return;
        }

        const casdoorConfig = {
          serverUrl: process.env.REACT_APP_CASDOOR_SERVER_URL || '',
          clientId: process.env.REACT_APP_CASDOOR_CLIENT_ID || '',
          organizationName: process.env.REACT_APP_CASDOOR_ORGANIZATION_NAME || '',
          appName: process.env.REACT_APP_CASDOOR_APP_NAME || '',
          redirectPath: '/callback',
        };

        if (!casdoorConfig.serverUrl || !casdoorConfig.clientId) {
          message.error('Admin Dashboard not configured');
          navigate('/login', { replace: true });
          return;
        }

        const sdk = new Sdk(casdoorConfig);
        const redirectUrl = `${window.location.origin}${casdoorConfig.redirectPath}`;

        // Check state in sessionStorage for debugging
        const savedState = sessionStorage.getItem('admin_auth_state');
        console.log('Callback received:', {
          code,
          state,
          savedState,
          redirectUrl,
          currentUrl: window.location.href,
          stateMatch: state === savedState
        });

        // Exchange code for token using SDK
        // exchangeForAccessToken automatically reads code and state from window.location.href
        // It also verifies state from sessionStorage
        try {
          console.log('Calling exchangeForAccessToken...');
          // Pass state explicitly if needed (SDK should handle it automatically)
          const tokenResponse = await sdk.exchangeForAccessToken();
          console.log('Token response:', tokenResponse ? 'received' : 'null');

          if (tokenResponse && tokenResponse.access_token) {
            const accessToken = tokenResponse.access_token;
            
            // Call backend API to set httpOnly cookie instead of storing in localStorage
            const apiUrl = process.env.REACT_APP_AZINSIGHT_API;
            if (!apiUrl) {
              throw new Error('API URL not configured');
            }

            const setSessionResponse = await fetch(`${apiUrl}/api/v1/admin/auth/set-session`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include', // Important: include cookies
              body: JSON.stringify({ token: accessToken }),
            });

            if (!setSessionResponse.ok) {
              const errorData = await setSessionResponse.json();
              throw new Error(errorData.message || 'Failed to set session');
            }

            message.success('Login successful');
            navigate('/user-management', { replace: true });
          } else {
            console.error('No access_token in response:', tokenResponse);
            throw new Error('Failed to get access token from SDK response');
          }
        } catch (tokenError: any) {
          console.error('Token exchange error:', tokenError);
          message.error(tokenError.message || 'Authentication failed');
          navigate('/login', { replace: true });
        }
      } catch (error: any) {
        console.error('Callback error:', error);
        message.error(error.message || 'Authentication failed');
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: '#f0f2f5'
    }}>
      <div style={{ textAlign: 'center' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>Processing authentication...</p>
      </div>
    </div>
  );
};

export default Callback;
